import { useEffect, useMemo, useRef } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import * as THREE from "three";
import { roomColor, planBounds, markerColor } from "@/lib/floorplan";

function Controls() {
  const { camera, gl } = useThree();
  const ref = useRef();
  useEffect(() => {
    const c = new OrbitControls(camera, gl.domElement);
    c.enableDamping = true;
    c.dampingFactor = 0.08;
    c.maxPolarAngle = Math.PI / 2.05;
    c.minDistance = 3;
    c.maxDistance = 80;
    ref.current = c;
    return () => c.dispose();
  }, [camera, gl]);
  useFrame(() => ref.current?.update());
  return null;
}

function Room({ room, center }) {
  const w = room.w, d = room.h, hgt = room.wall_height || 2.4;
  const edges = useMemo(() => new THREE.EdgesGeometry(new THREE.BoxGeometry(w, hgt, d)), [w, hgt, d]);
  const cx = room.x + w / 2 - center.x;
  const cz = room.y + d / 2 - center.y;
  return (
    <group position={[cx, hgt / 2, cz]}>
      <mesh>
        <boxGeometry args={[w, hgt, d]} />
        <meshStandardMaterial color={roomColor(room.room_type)} transparent opacity={0.78} roughness={0.5} metalness={0.05} />
      </mesh>
      <lineSegments geometry={edges}>
        <lineBasicMaterial color="#1e293b" />
      </lineSegments>
    </group>
  );
}

function Marker({ marker, center }) {
  const col = markerColor(marker.issue_type);
  const rad = marker.severity === "high" ? 0.32 : marker.severity === "medium" ? 0.26 : 0.2;
  const cx = marker.x - center.x, cz = marker.y - center.y;
  return (
    <group position={[cx, 0, cz]}>
      <mesh position={[0, 1.5, 0]}>
        <sphereGeometry args={[rad, 16, 16]} />
        <meshStandardMaterial color={col} emissive={col} emissiveIntensity={0.35} roughness={0.4} />
      </mesh>
      <mesh position={[0, 0.75, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 1.5, 6]} />
        <meshStandardMaterial color={col} />
      </mesh>
    </group>
  );
}

function Scene({ rooms, markers }) {
  const b = useMemo(() => planBounds(rooms), [rooms]);
  const span = Math.ceil(b.maxDim * 1.6);
  return (
    <>
      <ambientLight intensity={0.78} />
      <directionalLight position={[12, 20, 10]} intensity={0.85} />
      <group position={[0, -0.01, 0]}>
        <gridHelper args={[span, span, "#cbd5e1", "#e2e8f0"]} />
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[span, span]} />
          <meshStandardMaterial color="#f8fafc" roughness={0.9} />
        </mesh>
      </group>
      {rooms.map((r) => <Room key={r.id} room={r} center={b.center} />)}
      {(markers || []).map((m) => <Marker key={m.id} marker={m} center={b.center} />)}
      <Controls />
    </>
  );
}

export function FloorPlan3DView({ rooms = [], markers = [], height = 380 }) {
  const b = planBounds(rooms);
  const dcam = Math.max(b.maxDim * 1.35, 7);
  return (
    <div className="overflow-hidden rounded-md border border-slate-200" style={{ height }} data-testid="floorplan-3d-canvas">
      <Canvas camera={{ position: [dcam, dcam * 0.95, dcam], fov: 45 }} gl={{ preserveDrawingBuffer: true, antialias: true }}>
        <Scene rooms={rooms} markers={markers} />
      </Canvas>
    </div>
  );
}
